import { useEffect, useMemo, useState } from 'react'
import { api } from '../../../lib/api.js'
import { toast } from 'sonner'

export function useImages(initialTypeId = '') {
  const [types, setTypes] = useState([])
  const [typeId, setTypeId] = useState(initialTypeId)
  const [images, setImages] = useState([])
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState(null)

  useEffect(() => {
    api.get('/admin/image-types').then(({ data }) => {
      // Only show active slots in dropdowns
      setTypes((data.data || []).filter((t) => t.is_active))
    })
  }, [])

  const load = async (page = 1, filters = {}) => {
    setLoading(true)
    try {
      const params = { page }
      if (typeId) params.image_type_id = typeId
      if (filters.search) params.search = filters.search
      if (filters.filterActive !== '') params.is_active = filters.filterActive
      const { data } = await api.get('/admin/images', { params })
      setImages(data.data.data || data.data)
      setPagination(data.data)
    } finally { setLoading(false) }
  }

  useEffect(() => { load(1) }, [typeId])

  const currentType = useMemo(
    () => types.find((t) => String(t.id) === String(typeId)),
    [types, typeId]
  )
  const activeCount = images.filter((i) => i.is_active).length
  const atCapacity = currentType && !currentType.is_unlimited && activeCount >= currentType.max_active

  // Optimistic toggle — flip locally first, revert on error (no full reload)
  const toggleActive = async (img) => {
    const next = !img.is_active
    setImages((prev) => prev.map((i) => i.id === img.id ? { ...i, is_active: next } : i))
    try {
      const { data } = await api.patch(`/admin/images/${img.id}/toggle-active`)
      // Sync exact server state in case backend applied limit constraints
      setImages((prev) => prev.map((i) => i.id === img.id ? { ...i, ...data.data } : i))
      toast.success(img.is_active ? 'Gambar dinonaktifkan' : 'Gambar diaktifkan')
    } catch (err) {
      // Revert on failure
      setImages((prev) => prev.map((i) => i.id === img.id ? { ...i, is_active: img.is_active } : i))
      toast.error(err.response?.data?.message || 'Gagal mengubah status')
    }
  }

  // Optimistic remove — remove locally first, revert on error
  const remove = async (img) => {
    if (!confirm(`Hapus gambar "${img.title || img.original_name}"?`)) return
    setImages((prev) => prev.filter((i) => i.id !== img.id))
    try {
      await api.delete(`/admin/images/${img.id}`)
      toast.success('Gambar berhasil dihapus')
    } catch (err) {
      // Revert — re-insert at original position requires the full list; do a silent reload
      load(pagination?.current_page ?? 1)
      toast.error(err.response?.data?.message || 'Gagal menghapus')
    }
  }

  return {
    types, typeId, setTypeId, images, loading, pagination,
    currentType, atCapacity,
    load, toggleActive, remove,
  }
}
